import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import {
  collection, addDoc, updateDoc, deleteDoc, doc,
  onSnapshot, query, serverTimestamp
} from 'firebase/firestore';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { db, auth } from './firebase';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import LoadingSpinner from './components/LoadingSpinner';
import Modal from './components/Modal';
import NotesBoard from './NotesBoard';
import { buildPermalink, copyToClipboard, pinEntityToDesktop } from './utils/shortcutHelpers';
import { printHtml } from './utils/printUtils';
import { effectiveFolderId, buildFolderMoveUpdate } from './utils/folderAssignment';
import { uploadImage } from './services/imageUploadService';
import './NotesApp.css';

const CATEGORIES = ['Personal', 'Work', 'Project', 'Meeting', 'Documentation'];

function NotesApp() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const routeParams = useParams(); // Build 26: /notes/:noteId path-param permalinks
  const [user, setUser] = useState(null);

  // Build 28 (item 2): ReactQuill ref + custom image handler. Images go
  // to Firebase Storage via the shared imageUploadService (validate →
  // 500KB compress → upload → URL); the URL is inserted into the editor
  // — NOT base64-embedded, which would bloat the Firestore note doc and
  // risk the 1MB limit (the problem fought in Build 24).
  const quillRef = useRef(null);

  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();
    input.onchange = async () => {
      const file = input.files && input.files[0];
      if (!file) return;
      const editor = quillRef.current && quillRef.current.getEditor();
      const range = editor ? editor.getSelection(true) : null;
      const PLACEHOLDER = 'Uploading image…';
      try {
        if (editor && range) {
          editor.insertText(range.index, PLACEHOLDER, { italic: true });
        }
        const url = await uploadImage(file, {
          scope: 'notes',
          ownerId: (auth.currentUser && auth.currentUser.uid) || 'anonymous'
        });
        if (editor && range) {
          editor.deleteText(range.index, PLACEHOLDER.length);
          editor.insertEmbed(range.index, 'image', url);
          editor.setSelection(range.index + 1);
        }
      } catch (err) {
        console.error('Image upload failed:', err);
        alert(`Image upload failed: ${err?.message || 'unknown error'}`);
        if (editor) {
          const text = editor.getText();
          const idx = text.indexOf(PLACEHOLDER);
          if (idx >= 0) editor.deleteText(idx, PLACEHOLDER.length);
        }
      }
    };
  }, []);

  // Memoized so the toolbar + custom handler are stable across renders
  // (recreating modules would reset the editor).
  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'color': [] }, { 'background': [] }],
        ['link', 'image', 'code-block'],
        ['clean']
      ],
      handlers: { image: imageHandler }
    }
  }), [imageHandler]);

  const [userProfile, setUserProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null); // null = "All Notes"
  const [users, setUsers] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [sharingNote, setSharingNote] = useState(null);
  const [isFolderShareModalOpen, setIsFolderShareModalOpen] = useState(false);
  const [sharingFolder, setSharingFolder] = useState(null);
  const [showExportDropdown, setShowExportDropdown] = useState(null);
  const [viewOnlyNote, setViewOnlyNote] = useState(null);
  const [isFolderFormOpen, setIsFolderFormOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderName, setFolderName] = useState('');
  // Build 26: full-screen mode for the edit/view modal
  const [isModalFullscreen, setIsModalFullscreen] = useState(false);
  // Build 26: small transient feedback toast for Copy Link / Pin actions
  const [actionFeedback, setActionFeedback] = useState(null);
  
  // New: Search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [filterTag, setFilterTag] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '',
    tagsInput: '',
    folderId: null
  });
  
  const [shareData, setShareData] = useState({
    userEmail: '',
    permission: 'view'
  });

  const [folderShareData, setFolderShareData] = useState({
    userEmail: '',
    permission: 'view'
  });

  const role = userProfile?.role || null;

  // Auth listener
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const profileUnsub = onSnapshot(doc(db, 'users', currentUser.uid), (snap) => {
          if (snap.exists()) {
            setUserProfile({ id: snap.id, ...snap.data() });
          }
          setAuthLoading(false);
        });
        return () => profileUnsub();
      } else {
        setAuthLoading(false);
      }
    });
    return () => unsub();
  }, []);

  // Set page title
  useEffect(() => {
    document.title = 'Notes - Wixon & Cross Desk Suite';
  }, []);

  // Notes listener
  useEffect(() => {
    if (!user) return;
    
    const notesQuery = query(collection(db, 'notes'));
    
    const unsub = onSnapshot(notesQuery, snap => {
      const allNotes = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      const filteredNotes = allNotes.filter(note => 
        note.createdBy === user.uid || 
        note.sharedWith?.some(s => s.userId === user.uid)
      );
      
      setNotes(filteredNotes);
    });
    
    return () => unsub();
  }, [user]);

  // Folders listener
  useEffect(() => {
    if (!user) return;
    
    const foldersQuery = query(collection(db, 'noteFolders'));
    
    const unsub = onSnapshot(foldersQuery, snap => {
      const allFolders = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      // Show user's own folders AND folders shared with them
      const userFolders = allFolders.filter(folder => 
        folder.createdBy === user.uid || 
        folder.sharedWith?.some(s => s.userId === user.uid)
      );
      
      setFolders(userFolders);
    });
    
    return () => unsub();
  }, [user]);

  // Users listener
  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(collection(db, 'users'), snap => {
      setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [user]);

  // Open note from URL — supports both legacy ?noteId= query param AND
  // Build 26's /notes/:noteId path param (used by Copy Link permalinks).
  useEffect(() => {
    const noteId = routeParams.noteId || searchParams.get('noteId');
    if (!noteId || notes.length === 0) return;
    
    const note = notes.find(n => n.id === noteId);
    if (note) {
      // Open in edit mode
      handleEdit(note);
      // Clear the URL so refreshing doesn't re-trigger this on every render
      navigate('/notes', { replace: true });
    } else {
      console.log('Note not found with id:', noteId);
    }
  }, [searchParams, routeParams.noteId, notes, navigate]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/dashboard');
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const tags = formData.tagsInput
      .split(',')
      .map(t => t.trim())
      .filter(t => t.length > 0);
    
    try {
      if (editingNote) {
        await updateDoc(doc(db, 'notes', editingNote.id), {
          title: formData.title,
          content: formData.content,
          category: formData.category || null,
          tags: tags,
          folderId: formData.folderId || null,
          updatedAt: serverTimestamp()
        });
      } else {
        const targetFolderId = formData.folderId || currentFolder || null;
        
        // Check if folder is shared and auto-share note
        let initialSharedWith = [];
        if (targetFolderId) {
          const targetFolder = folders.find(f => f.id === targetFolderId);
          if (targetFolder && targetFolder.sharedWith && targetFolder.sharedWith.length > 0) {
            initialSharedWith = targetFolder.sharedWith;
          }
        }
        
        await addDoc(collection(db, 'notes'), {
          title: formData.title,
          content: formData.content,
          category: formData.category || null,
          tags: tags,
          folderId: targetFolderId,
          createdBy: user.uid,
          createdByEmail: user.email,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          sharedWith: initialSharedWith,
          pinned: false
        });
      }
      resetForm();
    } catch (err) {
      console.error('Error saving note:', err);
      alert('Error saving note');
    }
  };

  const resetForm = () => {
    setFormData({ title: '', content: '', category: '', tagsInput: '', folderId: currentFolder || null });
    setEditingNote(null);
    setIsFormOpen(false);
    setIsModalFullscreen(false); // Build 26: collapse fullscreen on close
  };

  // Build 26: small toast for Copy Link / Pin to Desktop feedback. Auto-dismisses.
  const flashFeedback = (type, text) => {
    setActionFeedback({ type, text });
    setTimeout(() => setActionFeedback(null), 2200);
  };

  // Build 26: Copy a shareable permalink for the currently-edited note.
  const handleCopyLink = async (note) => {
    if (!note?.id) return;
    const url = buildPermalink('/notes', note.id);
    const ok = await copyToClipboard(url);
    flashFeedback(ok ? 'success' : 'error', ok ? 'Link copied to clipboard' : 'Could not copy link');
  };

  // Build 26: Pin a note shortcut to the user's desktop in one click.
  const handlePinToDesktop = async (note) => {
    if (!note?.id || !user?.uid) return;
    try {
      await pinEntityToDesktop({
        userId: user.uid,
        type: 'note',
        entityId: note.id,
        title: note.title,
        icon: '📝'
      });
      flashFeedback('success', 'Pinned to desktop');
    } catch (err) {
      console.error('Pin failed:', err);
      flashFeedback('error', `Pin failed: ${err?.message || 'unknown'}`);
    }
  };

  // Build 27 (item 1): move a note into one of the current user's folders.
  // Uses buildFolderMoveUpdate so owners change the note's folderId while
  // recipients of a shared note only set their own slot in the
  // folderAssignments map — the owner's organization is untouched.
  const handleMoveNoteToFolder = async (note, targetFolderId) => {
    if (!note?.id || !user?.uid) return;
    try {
      const update = buildFolderMoveUpdate(note, user.uid, targetFolderId);
      await updateDoc(doc(db, 'notes', note.id), update);
      const destName = targetFolderId
        ? (folders.find(f => f.id === targetFolderId)?.name || 'folder')
        : 'My Notes';
      flashFeedback('success', `Moved to ${destName}`);
    } catch (err) {
      console.error('Move to folder failed:', err);
      flashFeedback('error', `Move failed: ${err?.message || 'unknown'}`);
    }
  };

  const handlePrintNote = (note, useFormData = false) => {
    const title = useFormData ? formData.title : note?.title;
    const content = useFormData ? formData.content : note?.content;
    const metaParts = [];
    if (formData.category || note?.category) {
      metaParts.push(`Category: ${formData.category || note?.category}`);
    }
    if (note?.createdAt?.toDate) {
      metaParts.push(`Created: ${note.createdAt.toDate().toLocaleString()}`);
    }
    if (note?.updatedAt?.toDate) {
      metaParts.push(`Last updated: ${note.updatedAt.toDate().toLocaleString()}`);
    }
    printHtml({
      title: title || 'Untitled note',
      bodyHtml: content || '<em>(no content)</em>',
      metaHtml: metaParts.join(' &nbsp;·&nbsp; ')
    });
  };

  const handleEdit = (note) => {
    setEditingNote(note);
    setFormData({
      title: note.title,
      content: note.content,
      category: note.category || '',
      tagsInput: note.tags?.join(', ') || '',
      folderId: note.folderId || null
    });
    setIsFormOpen(true);
  };

  const handleDelete = async (note) => {
    if (!window.confirm(`Delete note "${note.title}"?`)) return;
    
    try {
      await deleteDoc(doc(db, 'notes', note.id));
    } catch (err) {
      console.error('Error deleting note:', err);
      alert('Error deleting note');
    }
  };

  const handleTogglePin = async (note) => {
    try {
      await updateDoc(doc(db, 'notes', note.id), {
        pinned: !note.pinned,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error toggling pin:', err);
      alert('Error pinning note');
    }
  };

  const openShareModal = (note) => {
    setSharingNote(note);
    setShareData({ userEmail: '', permission: 'view' });
    setIsShareModalOpen(true);
  };

  const handleShare = async (e) => {
    e.preventDefault();
    
    const targetUser = users.find(u => u.email === shareData.userEmail);
    if (!targetUser) {
      alert('User not found');
      return;
    }
    
    if (targetUser.id === user.uid) {
      alert('Cannot share with yourself');
      return;
    }
    
    const currentShared = sharingNote.sharedWith || [];
    const alreadyShared = currentShared.find(s => s.userId === targetUser.id);
    
    if (alreadyShared) {
      const updated = currentShared.map(s => 
        s.userId === targetUser.id 
          ? { ...s, permission: shareData.permission }
          : s
      );
      
      try {
        await updateDoc(doc(db, 'notes', sharingNote.id), {
          sharedWith: updated,
          updatedAt: serverTimestamp()
        });
        alert('Permission updated');
        setIsShareModalOpen(false);
      } catch (err) {
        console.error('Error updating share:', err);
        alert('Error updating share');
      }
    } else {
      try {
        await updateDoc(doc(db, 'notes', sharingNote.id), {
          sharedWith: [
            ...currentShared,
            {
              userId: targetUser.id,
              userEmail: targetUser.email,
              permission: shareData.permission
            }
          ],
          updatedAt: serverTimestamp()
        });
        alert('Note shared successfully');
        setIsShareModalOpen(false);
      } catch (err) {
        console.error('Error sharing note:', err);
        alert('Error sharing note');
      }
    }
  };

  // Folder Management Functions
  const handleCreateFolder = async (e) => {
    e.preventDefault();
    if (!folderName.trim()) return;

    try {
      await addDoc(collection(db, 'noteFolders'), {
        name: folderName,
        createdBy: user.uid,
        createdAt: serverTimestamp()
      });
      setFolderName('');
      setIsFolderFormOpen(false);
    } catch (err) {
      console.error('Error creating folder:', err);
      alert('Error creating folder');
    }
  };

  const handleEditFolder = async (e) => {
    e.preventDefault();
    if (!folderName.trim() || !editingFolder) return;

    try {
      await updateDoc(doc(db, 'noteFolders', editingFolder.id), {
        name: folderName
      });
      setFolderName('');
      setEditingFolder(null);
      setIsFolderFormOpen(false);
    } catch (err) {
      console.error('Error updating folder:', err);
      alert('Error updating folder');
    }
  };

  const handleDeleteFolder = async (folder) => {
    if (!window.confirm(`Delete folder "${folder.name}"?\n\nNotes in this folder will be moved to "My Notes".`)) return;

    try {
      // Move all notes in this folder to "My Notes" (folderId = null)
      const folderNotes = notes.filter(n => n.folderId === folder.id);
      await Promise.all(
        folderNotes.map(note =>
          updateDoc(doc(db, 'notes', note.id), { folderId: null })
        )
      );

      // Delete the folder
      await deleteDoc(doc(db, 'noteFolders', folder.id));

      // If currently viewing this folder, switch to "My Notes"
      if (currentFolder === folder.id) {
        setCurrentFolder(null);
      }
    } catch (err) {
      console.error('Error deleting folder:', err);
      alert('Error deleting folder');
    }
  };

  const openEditFolderModal = (folder) => {
    setEditingFolder(folder);
    setFolderName(folder.name);
    setIsFolderFormOpen(true);
  };

  const closeFolderModal = () => {
    setIsFolderFormOpen(false);
    setEditingFolder(null);
    setFolderName('');
  };

  // Folder Sharing Functions
  const openFolderShareModal = (folder) => {
    setSharingFolder(folder);
    setFolderShareData({ userEmail: '', permission: 'view' });
    setIsFolderShareModalOpen(true);
  };

  const handleShareFolder = async (e) => {
    e.preventDefault();
    
    const targetUser = users.find(u => u.email === folderShareData.userEmail);
    if (!targetUser) {
      alert('User not found');
      return;
    }
    
    if (targetUser.id === user.uid) {
      alert('You cannot share a folder with yourself');
      return;
    }
    
    const alreadyShared = sharingFolder.sharedWith?.some(s => s.userId === targetUser.id);
    if (alreadyShared) {
      alert('Folder is already shared with this user');
      return;
    }
    
    try {
      const updatedSharedWith = [
        ...(sharingFolder.sharedWith || []),
        {
          userId: targetUser.id,
          userEmail: targetUser.email,
          permission: folderShareData.permission,
          sharedAt: new Date().toISOString()
        }
      ];
      
      // Update folder with new share
      await updateDoc(doc(db, 'noteFolders', sharingFolder.id), {
        sharedWith: updatedSharedWith
      });
      
      // Share all notes in this folder with the user
      const folderNotes = notes.filter(n => n.folderId === sharingFolder.id);
      await Promise.all(
        folderNotes.map(note => {
          const noteAlreadyShared = note.sharedWith?.some(s => s.userId === targetUser.id);
          if (!noteAlreadyShared) {
            return updateDoc(doc(db, 'notes', note.id), {
              sharedWith: [
                ...(note.sharedWith || []),
                {
                  userId: targetUser.id,
                  userEmail: targetUser.email,
                  permission: folderShareData.permission,
                  sharedAt: new Date().toISOString()
                }
              ]
            });
          }
          return Promise.resolve();
        })
      );
      
      setIsFolderShareModalOpen(false);
      setSharingFolder(null);
      alert(`Folder shared with ${targetUser.email}`);
    } catch (err) {
      console.error('Error sharing folder:', err);
      alert('Error sharing folder');
    }
  };

  const handleRemoveFolderShare = async (folder, shareEntry) => {
    if (!window.confirm(`Remove ${shareEntry.userEmail} from this folder?`)) return;
    
    try {
      const updatedSharedWith = folder.sharedWith.filter(s => s.userId !== shareEntry.userId);
      
      await updateDoc(doc(db, 'noteFolders', folder.id), {
        sharedWith: updatedSharedWith
      });
      
      alert(`Removed ${shareEntry.userEmail} from folder`);
    } catch (err) {
      console.error('Error removing folder share:', err);
      alert('Error removing share');
    }
  };

  const handleExport = (note, format) => {
    const filename = `note-${note.title.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}`;
    
    // Convert HTML to plain text (for txt export)
    const stripHtml = (html) => {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    };
    
    // Convert HTML to Markdown with proper formatting
    const htmlToMarkdown = (html) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      let listCounters = [0]; // Stack to track list numbering at each depth
      
      const processNode = (node, depth = 0) => {
        if (node.nodeType === Node.TEXT_NODE) {
          return node.textContent;
        }
        
        if (node.nodeType !== Node.ELEMENT_NODE) return '';
        
        const tag = node.tagName.toLowerCase();
        const indent = '   '.repeat(depth);
        
        switch (tag) {
          case 'h1':
          case 'h2':
          case 'h3': {
            const level = parseInt(tag[1]);
            const text = getText(node);
            return `\n${'#'.repeat(level)} ${text}\n\n`;
          }
          case 'strong':
          case 'b':
            return `**${getText(node)}**`;
          case 'em':
          case 'i':
            return `*${getText(node)}*`;
          case 'u':
            return `_${getText(node)}_`;
          case 's':
            return `~~${getText(node)}~~`;
          case 'p':
            return processChildren(node, depth) + '\n\n';
          case 'br':
            return '\n';
          case 'ol': {
            let result = '\n';
            let counter = 1;
            let letterCounters = {}; // Track letter counters for each indent level
            
            for (const child of node.children) {
              if (child.tagName.toLowerCase() === 'li') {
                // Check for Quill indent class
                const indentClass = Array.from(child.classList).find(c => c.startsWith('ql-indent-'));
                const indentLevel = indentClass ? parseInt(indentClass.replace('ql-indent-', '')) : 0;
                
                if (indentLevel > 0) {
                  // Nested item - use letters
                  if (!letterCounters[indentLevel]) letterCounters[indentLevel] = 1;
                  const letter = String.fromCharCode(96 + letterCounters[indentLevel]);
                  const indent = '   '.repeat(indentLevel);
                  const itemText = getListItemText(child, depth + indentLevel + 1);
                  result += `${indent}${letter}. ${itemText}`;
                  letterCounters[indentLevel]++;
                } else {
                  // Top-level item - use numbers
                  const itemText = getListItemText(child, depth + 1);
                  result += `${counter}. ${itemText}`;
                  counter++;
                  // Reset letter counters when we hit a new top-level item
                  letterCounters = {};
                }
              }
            }
            return result + '\n';
          }
          case 'ul': {
            let result = '\n';
            for (const child of node.children) {
              if (child.tagName.toLowerCase() === 'li') {
                const itemText = getListItemText(child, depth + 1);
                result += `${indent}- ${itemText}`;
              }
            }
            return result + (depth === 0 ? '\n' : '');
          }
          case 'a':
            return `[${getText(node)}](${node.href || ''})`;
          case 'code':
            return `\`${getText(node)}\``;
          case 'pre':
            return `\n\`\`\`\n${getText(node)}\n\`\`\`\n`;
          default:
            return processChildren(node, depth);
        }
      };
      
      const getListItemText = (li, depth) => {
        let text = '';
        let hasNested = false;
        
        for (const child of li.childNodes) {
          if (child.nodeType === Node.TEXT_NODE) {
            text += child.textContent.trim() + ' ';
          } else if (child.nodeType === Node.ELEMENT_NODE) {
            const tag = child.tagName.toLowerCase();
            if (tag === 'ol' || tag === 'ul') {
              hasNested = true;
              text += processNode(child, depth);
            } else if (tag === 'strong' || tag === 'b') {
              text += `**${child.textContent.trim()}** `;
            } else if (tag === 'em' || tag === 'i') {
              text += `*${child.textContent.trim()}* `;
            } else if (tag === 'u') {
              text += `_${child.textContent.trim()}_ `;
            } else if (tag === 's') {
              text += `~~${child.textContent.trim()}~~ `;
            } else {
              text += child.textContent.trim() + ' ';
            }
          }
        }
        
        return text.trim() + '\n';
      };
      
      const processChildren = (node, depth) => {
        return Array.from(node.childNodes).map(child => processNode(child, depth)).join('');
      };
      
      const getText = (node) => {
        let text = '';
        for (const child of node.childNodes) {
          if (child.nodeType === Node.TEXT_NODE) {
            text += child.textContent;
          } else if (child.nodeType === Node.ELEMENT_NODE) {
            const tag = child.tagName.toLowerCase();
            if (tag === 'strong' || tag === 'b') {
              text += `**${getText(child)}**`;
            } else if (tag === 'em' || tag === 'i') {
              text += `*${getText(child)}*`;
            } else if (tag === 'u') {
              text += `_${getText(child)}_`;
            } else if (tag === 's') {
              text += `~~${getText(child)}~~`;
            } else {
              text += getText(child);
            }
          }
        }
        return text;
      };
      
      let result = processNode(doc.body);
      result = result.replace(/\n{3,}/g, '\n\n').trim();
      return result;
    };
    
    switch (format) {
      case 'markdown':
        const mdContent = htmlToMarkdown(note.content);
        const md = `# ${note.title}\n\n${mdContent}\n\n---\n*Created by: ${note.createdByEmail}*\n*Last updated: ${note.updatedAt?.toDate?.().toLocaleString() || 'N/A'}*`;
        const mdBlob = new Blob([md], { type: 'text/markdown' });
        const mdUrl = URL.createObjectURL(mdBlob);
        const mdLink = document.createElement('a');
        mdLink.href = mdUrl;
        mdLink.download = `${filename}.md`;
        mdLink.click();
        URL.revokeObjectURL(mdUrl);
        break;
        
      case 'pdf':
        const { jsPDF } = window.jspdf || {};
        if (!jsPDF) {
          alert('PDF library not loaded. Please refresh and try again.');
          break;
        }
        
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 15;
        const maxWidth = pageWidth - (margin * 2);
        let yPosition = 20;
        
        // Title
        doc.setFontSize(18);
        doc.setFont(undefined, 'bold');
        doc.text(note.title, margin, yPosition);
        yPosition += 10;
        
        // Metadata
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(100);
        doc.text(`Created by: ${note.createdByEmail}`, margin, yPosition);
        yPosition += 5;
        doc.text(`Last updated: ${note.updatedAt?.toDate?.().toLocaleString() || 'N/A'}`, margin, yPosition);
        yPosition += 10;
        
        // Content - use Markdown conversion for better formatting
        doc.setFontSize(11);
        doc.setTextColor(0);
        const pdfContent = htmlToMarkdown(note.content);
        const lines = doc.splitTextToSize(pdfContent, maxWidth);
        
        // Add content with page breaks
        lines.forEach(line => {
          if (yPosition > pageHeight - 20) {
            doc.addPage();
            yPosition = 20;
          }
          doc.text(line, margin, yPosition);
          yPosition += 6;
        });
        
        doc.save(`${filename}.pdf`);
        break;
        
      case 'text':
        const txtContent = htmlToMarkdown(note.content);
        const txt = `${note.title}\n${'='.repeat(note.title.length)}\n\n${txtContent}\n\n---\nCreated by: ${note.createdByEmail}\nLast updated: ${note.updatedAt?.toDate?.().toLocaleString() || 'N/A'}`;
        const txtBlob = new Blob([txt], { type: 'text/plain' });
        const txtUrl = URL.createObjectURL(txtBlob);
        const txtLink = document.createElement('a');
        txtLink.href = txtUrl;
        txtLink.download = `${filename}.txt`;
        txtLink.click();
        URL.revokeObjectURL(txtUrl);
        break;
        
      default:
        break;
    }
    
    setShowExportDropdown(null);
  };

  const getFilteredAndSortedNotes = () => {
    let filtered = [...notes];
    
    // Filter by current folder. Build 27 (item 1): use the viewer's
    // effective folder (their personal assignment for shared notes, or
    // the note's own folderId otherwise) so each user organizes shared
    // notes independently.
    if (currentFolder === null) {
      // "My Notes" - show only notes the current user has not filed
      filtered = filtered.filter(note => !effectiveFolderId(note, user?.uid));
    } else {
      filtered = filtered.filter(note => effectiveFolderId(note, user?.uid) === currentFolder);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(note => {
        const plainContent = note.content.replace(/<[^>]*>/g, '');
        return (
          note.title.toLowerCase().includes(query) ||
          plainContent.toLowerCase().includes(query) ||
          note.tags?.some(tag => tag.toLowerCase().includes(query)) ||
          note.category?.toLowerCase().includes(query)
        );
      });
    }
    
    if (filterCategory !== 'All') {
      filtered = filtered.filter(note => note.category === filterCategory);
    }
    
    if (filterTag) {
      filtered = filtered.filter(note => 
        note.tags?.some(tag => tag.toLowerCase().includes(filterTag.toLowerCase()))
      );
    }
    
    filtered.sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      return (b.updatedAt?.toMillis?.() || 0) - (a.updatedAt?.toMillis?.() || 0);
    });
    
    return filtered;
  };

  const filteredNotes = getFilteredAndSortedNotes();

  if (authLoading) return <LoadingSpinner message="Loading..." />;
  if (!user || !userProfile) return <LoadingSpinner message="Setting up..." />;

  return (
    <div className="notes-app">
      <header className="header">
        <div className="header-content">
          <div>
            <div className="breadcrumb-nav-app">
              <button className="breadcrumb-item" onClick={() => navigate('/desktop')}>
                🖥️ Desktop
              </button>
              <span className="breadcrumb-separator">›</span>
              <button className="breadcrumb-item" onClick={() => navigate('/dashboard')}>
                📦 Dashboard
              </button>
              <span className="breadcrumb-separator">›</span>
              <span className="breadcrumb-item breadcrumb-current">📝 Notes</span>
            </div>
          </div>
          <div className="header-actions">
            <div className="search-box">
              <input
                type="text"
                placeholder="🔍 Search notes..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            <button className="btn btn-primary" onClick={() => setIsFormOpen(true)}>
              + New Note
            </button>
            <div className="user-info-section">
              <span className="user-email">{user.email}</span>
              <span className="user-role-label">Role: <span className="role-badge">{role}</span></span>
            </div>
            <button className="btn btn-secondary" onClick={handleSignOut}>
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <div className="notes-container">
        {/* Folder Sidebar */}
        <aside className="folders-sidebar">
          <div className="sidebar-header">
            <h3>Folders</h3>
          </div>
          
          <div className="sidebar-actions">
            <button 
              className="btn btn-secondary btn-small btn-full-width" 
              onClick={() => setIsFolderFormOpen(true)}
            >
              + New Folder
            </button>
          </div>
          
          <div className="folders-list">
            <div 
              className={`folder-item ${currentFolder === null ? 'active' : ''}`}
              onClick={() => setCurrentFolder(null)}
            >
              <span className="folder-icon">📝</span>
              <span className="folder-name">My Notes</span>
              <span className="folder-count">({notes.filter(n => !n.folderId).length})</span>
            </div>
            
            {folders.map(folder => {
              const folderNoteCount = notes.filter(n => effectiveFolderId(n, user?.uid) === folder.id).length;
              const isShared = folder.sharedWith && folder.sharedWith.length > 0;
              const isOwner = folder.createdBy === user.uid;
              
              return (
                <div 
                  key={folder.id}
                  className={`folder-item ${currentFolder === folder.id ? 'active' : ''}`}
                  onClick={() => setCurrentFolder(folder.id)}
                >
                  <span className="folder-icon">📂</span>
                  <span className="folder-name">
                    {folder.name}
                    {isShared && <span className="shared-indicator" title="Shared folder">👥</span>}
                  </span>
                  <span className="folder-count">({folderNoteCount})</span>
                  <div className="folder-actions" onClick={e => e.stopPropagation()}>
                    {isOwner && (
                      <>
                        <button 
                          className="btn-icon-small" 
                          onClick={() => openFolderShareModal(folder)}
                          title="Share Folder"
                        >
                          👥
                        </button>
                        <button 
                          className="btn-icon-small" 
                          onClick={() => openEditFolderModal(folder)}
                          title="Edit Folder"
                        >
                          ✏️
                        </button>
                        <button 
                          className="btn-icon-small" 
                          onClick={() => handleDeleteFolder(folder)}
                          title="Delete Folder"
                        >
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </aside>

        {/* Main Content */}
        <main className="main-content">
          <div className="notes-filters">
            <select 
              className="filter-select"
              value={filterCategory} 
              onChange={e => setFilterCategory(e.target.value)}
            >
              <option value="All">All Categories</option>
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <input
              type="text"
              className="filter-input"
              placeholder="Filter by tag..."
              value={filterTag}
              onChange={e => setFilterTag(e.target.value)}
            />
            <span className="results-count">
              {filteredNotes.length} note{filteredNotes.length !== 1 ? 's' : ''}
            </span>
          </div>
          
          <NotesBoard
            notes={filteredNotes}
            user={user}
            onEdit={handleEdit}
            onView={setViewOnlyNote}
            onDelete={handleDelete}
            onShare={openShareModal}
            onExport={handleExport}
            onTogglePin={handleTogglePin}
            onCopyLink={handleCopyLink}
            onPinToDesktop={handlePinToDesktop}
            onMoveToFolder={handleMoveNoteToFolder}
            myFolders={folders.filter(f => f.createdBy === user?.uid)}
            showExportDropdown={showExportDropdown}
            setShowExportDropdown={setShowExportDropdown}
          />
        </main>
      </div>

      <Modal
        isOpen={isFormOpen}
        onClose={resetForm}
        title={editingNote ? 'Edit Note' : 'New Note'}
        size={isModalFullscreen ? 'fullscreen' : 'large'}
        headerExtras={editingNote && (
          <>
            <button
              type="button"
              className="btn-modal-tool"
              onClick={() => handleCopyLink(editingNote)}
              title="Copy a shareable link to this note"
            >
              🔗 Copy Link
            </button>
            <button
              type="button"
              className="btn-modal-tool"
              onClick={() => handlePinToDesktop(editingNote)}
              title="Pin this note to your desktop"
            >
              📎 Pin to Desktop
            </button>
            <button
              type="button"
              className="btn-modal-tool"
              onClick={() => handlePrintNote(editingNote, true)}
              title="Print this note"
            >
              🖨️ Print
            </button>
            <button
              type="button"
              className="btn-modal-tool"
              onClick={() => setIsModalFullscreen(v => !v)}
              title={isModalFullscreen ? 'Exit fullscreen' : 'Expand to fullscreen'}
            >
              {isModalFullscreen ? '🗗 Exit' : '🗖 Fullscreen'}
            </button>
          </>
        )}
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              placeholder="Note title"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Folder</label>
              <select
                name="folderId"
                value={formData.folderId || ''}
                onChange={e => setFormData(prev => ({ ...prev, folderId: e.target.value || null }))}
              >
                <option value="">No Folder</option>
                {folders.map(folder => (
                  <option key={folder.id} value={folder.id}>{folder.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Category</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
              >
                <option value="">No Category</option>
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Tags</label>
              <input
                type="text"
                name="tagsInput"
                value={formData.tagsInput}
                onChange={handleInputChange}
                placeholder="e.g., urgent, todo, review"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Content *</label>
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={formData.content}
              onChange={(content) => setFormData(prev => ({ ...prev, content }))}
              modules={quillModules}
              style={{ height: '200px', marginBottom: '50px' }}
            />
          </div>

          <div className="form-actions" style={{ marginTop: '60px' }}>
            <button type="button" className="btn btn-secondary" onClick={resetForm}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {editingNote ? 'Update Note' : 'Create Note'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        title={`Share "${sharingNote?.title}"`}
      >
        <form onSubmit={handleShare}>
          <div className="form-group">
            <label>Share with User *</label>
            <select
              value={shareData.userEmail}
              onChange={e => setShareData(prev => ({ ...prev, userEmail: e.target.value }))}
              required
            >
              <option value="">Select user...</option>
              {users.filter(u => u.id !== user.uid).map(u => (
                <option key={u.id} value={u.email}>{u.email}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Permission *</label>
            <select
              value={shareData.permission}
              onChange={e => setShareData(prev => ({ ...prev, permission: e.target.value }))}
              required
            >
              <option value="view">View Only</option>
              <option value="edit">Can Edit</option>
            </select>
          </div>

          {sharingNote?.sharedWith?.length > 0 && (
            <div className="shared-users-list">
              <h4>Currently Shared With:</h4>
              <ul>
                {sharingNote.sharedWith.map((s, idx) => (
                  <li key={idx}>
                    {s.userEmail} - <strong>{s.permission}</strong>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setIsShareModalOpen(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Share
            </button>
          </div>
        </form>
      </Modal>

      {/* View-Only Note Modal */}
      {viewOnlyNote && (
        <div className="modal-overlay" onClick={() => setViewOnlyNote(null)}>
          <div className="modal view-only-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>View Note</h3>
              <span className="view-only-badge">👁️ View Only</span>
              <div className="modal-header-extras">
                <button
                  type="button"
                  className="btn-modal-tool"
                  onClick={() => handleCopyLink(viewOnlyNote)}
                  title="Copy a shareable link to this note"
                >
                  🔗 Copy Link
                </button>
                <button
                  type="button"
                  className="btn-modal-tool"
                  onClick={() => handlePinToDesktop(viewOnlyNote)}
                  title="Pin this note to your desktop"
                >
                  📎 Pin to Desktop
                </button>
                <button
                  type="button"
                  className="btn-modal-tool"
                  onClick={() => handlePrintNote(viewOnlyNote, false)}
                  title="Print this note"
                >
                  🖨️ Print
                </button>
              </div>
              <button className="close-btn" onClick={() => setViewOnlyNote(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Title</label>
                <input type="text" value={viewOnlyNote.title} disabled />
              </div>
              <div className="form-group">
                <label>Category</label>
                <input type="text" value={viewOnlyNote.category || 'Uncategorized'} disabled />
              </div>
              <div className="form-group">
                <label>Content</label>
                <div 
                  className="note-content-readonly"
                  dangerouslySetInnerHTML={{ __html: viewOnlyNote.content }}
                />
              </div>
              {viewOnlyNote.sharedWith && viewOnlyNote.sharedWith.length > 0 && (
                <div className="form-group">
                  <label>Shared With</label>
                  <div className="shared-users-readonly">
                    {viewOnlyNote.sharedWith.map((s, idx) => (
                      <div key={idx} className="shared-user-item">
                        {s.userEmail} ({s.permission})
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="form-group">
                <label>Created</label>
                <input 
                  type="text" 
                  value={viewOnlyNote.createdAt?.toDate().toLocaleString() || 'Unknown'} 
                  disabled 
                />
              </div>
              <div className="form-group">
                <label>Last Updated</label>
                <input 
                  type="text" 
                  value={viewOnlyNote.updatedAt?.toDate().toLocaleString() || 'Unknown'} 
                  disabled 
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Folder Form Modal */}
      <Modal
        isOpen={isFolderFormOpen}
        onClose={closeFolderModal}
        title={editingFolder ? 'Edit Folder' : 'New Folder'}
      >
        <form onSubmit={editingFolder ? handleEditFolder : handleCreateFolder}>
          <div className="form-group">
            <label>Folder Name *</label>
            <input
              type="text"
              value={folderName}
              onChange={e => setFolderName(e.target.value)}
              required
              placeholder="Enter folder name"
              autoFocus
            />
          </div>
          <div className="modal-buttons">
            <button type="submit" className="btn btn-primary">
              {editingFolder ? 'Save' : 'Create'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={closeFolderModal}>
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      {/* Folder Share Modal */}
      <Modal
        isOpen={isFolderShareModalOpen}
        onClose={() => {
          setIsFolderShareModalOpen(false);
          setSharingFolder(null);
        }}
        title={`Share Folder: ${sharingFolder?.name || ''}`}
      >
        <form onSubmit={handleShareFolder}>
          <div className="form-group">
            <label>Share with User *</label>
            <select
              value={folderShareData.userEmail}
              onChange={e => setFolderShareData({ ...folderShareData, userEmail: e.target.value })}
              required
            >
              <option value="">Select user...</option>
              {users.filter(u => u.id !== user.uid).map(u => (
                <option key={u.id} value={u.email}>{u.email}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Permission *</label>
            <select
              value={folderShareData.permission}
              onChange={e => setFolderShareData({ ...folderShareData, permission: e.target.value })}
            >
              <option value="view">View Only</option>
              <option value="edit">Can Edit</option>
            </select>
          </div>
          <div className="modal-buttons">
            <button type="submit" className="btn btn-primary">
              Share Folder
            </button>
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={() => {
                setIsFolderShareModalOpen(false);
                setSharingFolder(null);
              }}
            >
              Cancel
            </button>
          </div>
        </form>

        {sharingFolder && sharingFolder.sharedWith && sharingFolder.sharedWith.length > 0 && (
          <div className="shared-with-section">
            <h4>Currently Shared With:</h4>
            <ul className="shared-with-list">
              {sharingFolder.sharedWith.map((shareEntry, idx) => (
                <li key={idx} className="shared-entry">
                  <div>
                    <strong>{shareEntry.userEmail}</strong>
                    <span className="permission-badge">{shareEntry.permission}</span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-small btn-secondary"
                    onClick={() => handleRemoveFolderShare(sharingFolder, shareEntry)}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
            <p className="info-text">
              ℹ️ All notes in this folder are automatically shared with these users
            </p>
          </div>
        )}
      </Modal>

      {/* Build 26: transient feedback toast for Copy Link / Pin to Desktop */}
      {actionFeedback && (
        <div className={`notes-action-toast notes-action-toast--${actionFeedback.type}`}>
          {actionFeedback.text}
        </div>
      )}
    </div>
  );
}

export default NotesApp;
